import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useSEO } from '../../shared/hooks/useSEO';
import { SEO_CONTENT, SeoPage } from '../../shared/constants/seo';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  SITE_URL,
  localizedPath,
  stripLanguage,
} from '../../shared/constants/routes';

/**
 * Meta tags for the current page.
 *
 * Built-in pages name a key from seo.json. Pages created in the CMS carry
 * their own title and description instead, so the union below insists on one
 * source or the other - a page with neither would ship a blank title.
 */
type SEOProps =
  | {
      page: SeoPage;
      customTitle?: string;
      customDescription?: string;
      customKeywords?: string;
    }
  | {
      page?: undefined;
      customTitle: string;
      customDescription: string;
      customKeywords?: string;
    };

export const SEO: React.FC<SEOProps> = ({
  page,
  customTitle,
  customDescription,
  customKeywords,
}) => {
  const { language } = useLanguage();
  const location = useLocation();

  // Canonical URLs always use the production origin so that preview deploys
  // never point search engines or scrapers at themselves.
  const { path } = stripLanguage(location.pathname);
  const currentUrl = `${SITE_URL}${localizedPath(path, language)}`;

  const alternates = [
    ...LANGUAGES.map((lang) => ({
      hreflang: lang,
      href: `${SITE_URL}${localizedPath(path, lang)}`,
    })),
    // x-default points at the default language so every page in the hreflang
    // set stays self-canonical. Keep in sync with scripts/prerender.js.
    { hreflang: 'x-default', href: `${SITE_URL}${localizedPath(path, DEFAULT_LANGUAGE)}` },
  ];

  const seoData = page ? SEO_CONTENT[language][page] : undefined;

  const title = customTitle || seoData?.title || '';
  const description = customDescription || seoData?.description || '';
  const keywords = customKeywords || seoData?.keywords;

  useSEO({
    title,
    description,
    keywords,
    url: currentUrl,
    type: seoData?.type ?? 'website',
    language,
    image: `${SITE_URL}/og-image.png`,
    alternates,
  });

  return null; // This component doesn't render anything
};
